import { createFileRoute } from "@tanstack/react-router";
import { ContentPage } from "@/components/ContentPage";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import { CallbackRail } from "@/components/CallbackRail";
import { IMAGES } from "@/lib/images";

const FAQS: FaqItem[] = [
  {
    q: "Vad är fönsterfilm egentligen?",
    a: "Fönsterfilm är en tunn, självhäftande polyesterfilm som monteras invändigt eller utvändigt på befintligt glas. Filmen fungerar som ett osynligt lager som förändrar glasets egenskaper — värmereducering, UV-skydd, splitterskydd, insynsskydd eller dekor — utan att glaset behöver bytas ut.",
  },
  {
    q: "Vilka typer av fönsterfilm finns det?",
    a: "Grundtyperna är solfilm (värme, UV, bländning), säkerhetsfilm (splitterskydd, inbrottsskydd, personsäkerhet) och dekorfilm (frostat glas, logotyper, insynsskydd). Du kan också kombinera filmerna, till exempel säkerhetsfilm med solfilm, för att få både skydd och komfort på samma glasyta.",
  },
  {
    q: "Ger fönsterfilm insynsskydd?",
    a: "Ja. Reflekterande solfilm ger dagsljus-insynsskydd (man ser inte in när det är ljusare ute än inne). Frostfilm eller mattad dekorfilm ger permanent insynsskydd oavsett ljus, samtidigt som dagsljuset släpps in. För fullt insynsskydd dygnet runt kombineras filmen med gardin eller ridå.",
  },
  {
    q: "Hur länge håller fönsterfilm?",
    a: "Invändig kvalitetsfilm från 3M eller Solar Screen håller normalt 15–20 år med rätt montering. Utvändig film är mer utsatt för väder och UV — vanligen 5–10 år beroende på fabrikat och exponering. BBN Nordic lämnar upp till 15 års totalgaranti på våra projekt.",
  },
  {
    q: "Skadar filmen glaset?",
    a: "Nej, rätt monterad film går att ta bort utan att skada glaset eller lämna spår. Till skillnad från etsning och sandblästring förändrar filmen alltså inte glasytan permanent.",
  },
  {
    q: "Kan filmen monteras på isolerglas?",
    a: "Ja, men vi behöver bedöma glastypen först. Vissa filmer, särskilt absorberande solfilm, kan öka den termiska belastningen på isolerglas. Vi väljer en film som passar dina rutor tekniskt så att glasgarantin bevaras.",
  },
];

export const Route = createFileRoute("/om-fonsterfilm")({
  head: () => ({
    meta: [
      { title: "Om fönsterfilm — hur den fungerar, typer och livslängd | BBN Nordic" },
      { name: "description", content: "Grundläggande om fönsterfilm: solfilm, säkerhetsfilm och dekorfilm. Hur den fungerar, hur länge den håller och när insynsskydd, splitterskydd eller värmereducering är rätt val." },
      { property: "og:title", content: "Om fönsterfilm — grundläggande guide" },
      { property: "og:description", content: "Vad fönsterfilm är, hur den fungerar och när du väljer solfilm, säkerhetsfilm eller dekorfilm." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://bbnnordic.se/om-fonsterfilm" },
      { property: "og:image", content: IMAGES.solfilm },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/om-fonsterfilm" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqJsonLd(FAQS)) }],
  }),
  component: () => (
    <ContentPage
      eyebrow="OM FÖNSTERFILM"
      title="Fönsterfilm — grundläggande guide."
      subtitle="Här går vi igenom hur fönsterfilm fungerar och hur länge den håller. Du får också veta när filmen kan hjälpa med insynsskydd, splitterskydd och värmereducering i din fastighet."
      heroImage={IMAGES.solfilm}
      breadcrumbs={[{ label: "Om fönsterfilm" }]}
      sections={[
        {
          eyebrow: "VAD ÄR FÖNSTERFILM",
          heading: "Fönsterfilm monteras på glaset du redan har.",
          paragraphs: [
            "Fönsterfilm är en självhäftande polyesterfilm som vi monterar direkt på ditt befintliga glas. Placeringen, invändigt eller utvändigt, beror på filmtyp och användning. Filmen är typiskt 50–350 mikron tunn och kan ge värmereducering, UV-skydd, splitterskydd, insynsskydd eller dekor.",
            "När filmen ska bytas kan vi ta bort den utan spår. Det gäller alla filmer vi arbetar med, och glaset återgår till sitt ursprungliga skick.",
          ],
          bullets: [
            "Polyester (PET) med metalliserad, färgad eller keramisk beläggning",
            "Monteras invändigt (vanligast) eller utvändigt",
            "Fullt avlägsnbar utan skada på glaset",
            "Livslängd 15–20 år invändigt, 5–10 år utvändigt",
          ],
        },
        {
          eyebrow: "INSYNSSKYDD",
          heading: "Insynsskydd som släpper igenom dagsljus.",
          paragraphs: [
            "Många väljer fönsterfilm för att minska insynen. Reflekterande solfilm ger insynsskydd när det är ljusare ute än inne, som det brukar vara under kontorsdagen. Frostfilm och mattad dekorfilm ger permanent insynsskydd oavsett ljusförhållanden och släpper samtidigt in dagsljus.",
            "Vi monterar ofta insynsskydd i gatunära bottenvåningar, receptioner, samtalsrum, badrum, konferensrum och på glasväggar mellan kontor. Vilken film vi föreslår beror på hur mycket dagsljus och utsikt du vill behålla.",
          ],
          bullets: [
            "Reflekterande solfilm — dagsljus-insynsskydd, behåller utsikten",
            "Frostfilm / mattad film — permanent insynsskydd, ljuset släpps in",
            "Dubbelsidig frostat — insynsskydd åt båda hållen",
            "One-way-film — spegeleffekt utåt, klarsikt inåt (kräver rätt ljusförhållanden)",
          ],
        },
        {
          eyebrow: "TYPER AV FILM",
          heading: "Solfilm, säkerhetsfilm och dekorfilm — tre huvudkategorier.",
          paragraphs: [
            "De tre huvudtyperna löser olika problem, men kan också kombineras. En kombinerad säkerhets- och solfilm ger till exempel både splitterskydd och värmereducering på samma glasyta.",
          ],
          bullets: [
            "Solfilm — reducerar värme (upp till 85 % TSER), UV (99 %+) och bländning",
            "Säkerhetsfilm — håller ihop glassplitter, motverkar inbrott och personskador",
            "Dekorfilm — frostat, mönster, logotyper, manifestationsband (AFS 2020:1)",
            "Kombinationsfilm — säkerhet + solskydd på samma folie",
          ],
        },
        {
          eyebrow: "NÄR VÄLJA VAD",
          heading: "Vanliga situationer och rätt filmval.",
          bullets: [
            "Överhettat kontor eller mötesrum → Solfilm",
            "Bländning på skärmar → Solfilm eller blackout-film på delytor",
            "Bleknande möbler, textilier, konst → UV-skyddande klarfilm eller solfilm",
            "Butiksfönster i bottenplan → Säkerhetsfilm mot inbrott och glaskross",
            "Skolor och offentliga miljöer → Säkerhetsfilm för personsäkerhet",
            "Kontorsglas och mötesrum → Dekorfilm med manifestationsband (AFS 2020:1)",
            "Badrum, samtalsrum → Frostfilm för insynsskydd",
            "Skyltfönster med logotyp → Skuren dekorfilm i färgad eller frostad folie",
          ],
        },
        {
          eyebrow: "LIVSLÄNGD & GARANTI",
          heading: "Kvalitetsfilm håller upp till 20 år invändigt.",
          paragraphs: [
            "Livslängden beror på filmtyp, glastyp, exponering och montering. Invändig kvalitetsfilm från 3M eller Solar Screen håller typiskt 15–20 år. Utvändig film utsätts för mer UV och väder och håller vanligen 5–10 år beroende på fabrikat.",
            "Vi på BBN Nordic är auktoriserade installatörer för 3M sedan 2008. På våra projekt lämnar vi upp till 15 års totalgaranti som omfattar material, montering och delaminering.",
          ],
          bullets: [
            "3M invändig solfilm — 15 år garanti",
            "3M säkerhetsfilm — 15 år garanti",
            "Solar Screen utvändig — 5–10 år beroende på produkt",
            "Solar Screen Safe 4XC (säkerhetsfilm utvändigt) — 3 år",
          ],
        },
      ]}
      related={[
        { to: "/solfilm-foretag", label: "Solfilm företag", desc: "Värme, UV och bländning i kontor och lokaler." },
        { to: "/solfilm-privat", label: "Solfilm privat", desc: "3M Prestige-serien för villa och lägenhet." },
        { to: "/sakerhetsfilm", label: "Säkerhetsfilm", desc: "Splitterskydd, inbrottsskydd och personsäkerhet." },
        { to: "/dekorfilm", label: "Dekorfilm", desc: "Frostat, logotyper och manifestationsband." },
      ]}
    >
      <FaqSection heading="Vanliga frågor om fönsterfilm." items={FAQS} />
      <CallbackRail product="Övrigt" />
    </ContentPage>
  ),
});
